// Feature Slider Alpine.js Component
document.addEventListener('alpine:init', () => {
  Alpine.data('featureSlider', (config = {}) => ({
    // Configuration
    autoplay: config.autoplay ?? true,
    interval: config.interval ?? 5000,
    showArrows: config.show_arrows ?? true,
    showDots: config.show_dots ?? true,
    transitionType: config.transition_type ?? 'slide',
    
    // State
    currentSlide: 0,
    totalSlides: 0,
    isPlaying: false,
    progress: 0,
    autoplayTimer: null,
    progressTimer: null,
    isPaused: false,
    parallaxOffset: 0,
    
    // Initialize
    init() {
      this.totalSlides = this.$el.querySelectorAll('.c-feature-slider__slide').length;
      
      if (this.totalSlides <= 1) {
        return;
      }
      
      // Add bouncy class if needed
      if (this.transitionType === 'bouncy') {
        this.$el.classList.add('c-feature-slider--bouncy');
      } else if (this.transitionType === 'fade') {
        this.$el.classList.add('c-feature-slider--fade');
        this.initFadeMode();
      }
      
      // Start autoplay if enabled
      if (this.autoplay) {
        this.startAutoplay();
      }
      
      // Pause on hover
      this.$el.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.$el.addEventListener('mouseleave', () => this.resumeAutoplay());
      
      // Pause on focus (accessibility)
      this.$el.addEventListener('focusin', () => this.pauseAutoplay());
      this.$el.addEventListener('focusout', () => this.resumeAutoplay());
      
      // Handle visibility change (pause when tab is not visible)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.resumeAutoplay();
        }
      });
      
      // Touch/swipe support
      this.initTouchSupport();
    },
    
    // Initialize fade mode
    initFadeMode() {
      const slides = this.$el.querySelectorAll('.c-feature-slider__slide');
      slides.forEach((slide, index) => {
        if (index === 0) {
          slide.classList.add('c-feature-slider__slide--active');
        }
      });
    },
    
    // Navigation methods
    nextSlide() {
      if (this.totalSlides <= 1) return;
      
      this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
      this.updateSlide();
      this.resetAutoplay();
    },
    
    previousSlide() {
      if (this.totalSlides <= 1) return;
      
      this.currentSlide = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
      this.updateSlide();
      this.resetAutoplay();
    },
    
    goToSlide(index) {
      if (this.totalSlides <= 1 || index === this.currentSlide) return;
      
      this.currentSlide = index;
      this.updateSlide();
      this.resetAutoplay();
    },
    
    // Update slide display
    updateSlide() {
      if (this.transitionType === 'fade') {
        this.updateFadeSlide();
      }
      // For slide transition, the CSS transform is handled by Alpine's :style binding
    },
    
    // Update fade slide
    updateFadeSlide() {
      const slides = this.$el.querySelectorAll('.c-feature-slider__slide');
      slides.forEach((slide, index) => {
        slide.classList.toggle('c-feature-slider__slide--active', index === this.currentSlide);
      });
    },
    
    // Autoplay methods
    startAutoplay() {
      if (!this.autoplay || this.totalSlides <= 1) return;
      
      this.isPlaying = true;
      this.startProgressTimer();
      
      this.autoplayTimer = setInterval(() => {
        if (!this.isPaused) {
          this.nextSlide();
        }
      }, this.interval);
    },
    
    stopAutoplay() {
      this.isPlaying = false;
      this.clearTimers();
    },
    
    pauseAutoplay() {
      this.isPaused = true;
      this.clearProgressTimer();
    },
    
    resumeAutoplay() {
      if (this.isPlaying && this.autoplay) {
        this.isPaused = false;
        this.startProgressTimer();
      }
    },
    
    resetAutoplay() {
      if (this.autoplay && this.totalSlides > 1) {
        this.clearTimers();
        this.startAutoplay();
      }
    },
    
    // Progress timer for progress bar
    startProgressTimer() {
      if (!this.autoplay) return;
      
      this.progress = 0;
      const progressStep = 100 / (this.interval / 50); // Update every 50ms
      
      this.progressTimer = setInterval(() => {
        if (!this.isPaused) {
          this.progress += progressStep;
          if (this.progress >= 100) {
            this.progress = 100;
            this.clearProgressTimer();
          }
        }
      }, 50);
    },
    
    clearProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    
    clearTimers() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      this.clearProgressTimer();
    },
    
    // Touch/swipe support
    initTouchSupport() {
      let startX = 0;
      let startY = 0;
      let endX = 0;
      let endY = 0;
      const minSwipeDistance = 50;
      
      this.$el.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });
      
      this.$el.addEventListener('touchend', (e) => {
        endX = e.changedTouches[0].clientX;
        endY = e.changedTouches[0].clientY;
        
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        
        // Check if horizontal swipe is more significant than vertical
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
          if (deltaX > 0) {
            this.previousSlide();
          } else {
            this.nextSlide();
          }
        }
      }, { passive: true });
    },
    
    // Parallax effect
    handleParallax() {
      const rect = this.$el.getBoundingClientRect();
      const scrollY = window.scrollY;
      
      // Only apply parallax when slider is in viewport
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        // Calculate parallax offset (moves slower than scroll)
        const elementTop = scrollY + rect.top;
        this.parallaxOffset = (scrollY - elementTop) * 0.3;
      }
    },
    
    // Cleanup on destroy
    destroy() {
      this.clearTimers();
    }
  }));
});
